/* eslint-disable @typescript-eslint/no-unsafe-assignment */
import React, { useCallback, useEffect, useState } from "react";
import Api from "../../../utils/Api";
import SessionStore from "../../../stores/SessionStore";
import { purgeSyncData } from "../../../utils/api/Purge";
import { Box, InlineNotification, Link, Text, useToast, useTranslation } from "@familyzone/component-library";
import { SyncStatus } from "./SyncStatus";
import { SyncCredentialStatus } from "./SyncCredentialStatus";
import PageWithHeader from "../../templates/PageWithHeader";
import { SubmitHandler, useForm } from "react-hook-form";
import { Button, Divider, Accordion, AccordionItem, AccordionButton, AccordionIcon, AccordionPanel, Flex } from "@chakra-ui/react";
import DumbBusyIndicator from "../../../modules/DumbBusyIndicator";
import { CheckBoxControl } from "./ConfigForm/CheckboxControl";
import { TextFieldControl } from "./ConfigForm/TextFieldControl";
import { CheckboxTextComboControl } from "./ConfigForm/CheckboxTextComboControl";
import { AdvanceConfigurationConfirmModal } from "./ConfigForm/AdvanceConfigurationConfirmModal";
import { PurgeConfirmModal } from "./ConfigForm/PurgeConfirmModal";
import { MergeOptions, UserMergeKey } from "./ConfigForm/MergeOptions";
import { colors } from "../../../theme";
import { useSessionStore } from "../../../storez/SessionStore";
import { executeSync, extractSyncError, getSyncByType, revokeCredential, startOAuth, SyncType } from "../../../utils/api/SyncApi";
import { isCredentialLinked, mapNodeToGoogleInputs, saveGoogleSync } from "./googleAzureSyncMapper";

export type GoogleConfigInputs = {
  enabled: boolean;
  sync_ous: boolean;
  allow_insecure_chrome: boolean;
  classroom_extension_login: boolean;
  sync_classrooms: boolean;
  sync_by_id: boolean;
  admin_username: string;
  oauth_client_id?: string;
  oauth_client_secret?: string;
  ignore_empty_groups: boolean;
  ignore_empty_classrooms: boolean;
  sync_id: string;

  strip_domain: boolean;
  strip_domain_values?: string; // these are commas seperated lists :puke:

  domain_filter: boolean;
  domain_filter_values?: string; // these are commas seperated lists :puke:

  ou_filter: boolean;
  ou_filter_value?: string;

  user_merge_key: UserMergeKey;
  user_merge_priority: number;
  user_merge_expression?: string;
  user_merge_attach_only: boolean;
};

// `allow_insecure_chrome` (FZ Auth API) and `classroom_extension_login` have no
// sync-api v2 equivalent, so they're read from / written to a narrow Flask endpoint
// that touches only these two fields. This is temporary — both are expected to
// disappear with the Flask app, so the hybrid path is deliberately kept to one read on
// load and one conditional write on save, isolated from the authoritative sync-api calls.
export const GOOGLE_CHROME_TOGGLES_URL = "/config/ajax/authentication/google/chrome";

type GoogleFlaskToggles = Pick<GoogleConfigInputs, "allow_insecure_chrome" | "classroom_extension_login">;

/** Wire shape of the Chrome toggles endpoint (both directions). */
export interface GoogleChromeToggles {
  google_allow_insecure_chrome?: boolean;
  google_classroom_extension_login?: boolean;
}

/**
 * Read the Flask-only toggles, or `undefined` when Flask can't be read. "Unknown" has
 * to stay distinguishable from "off": the save path writes these values back, so a
 * failed read collapsing to `false` would silently clear them on the next save.
 */
const readGoogleFlaskToggles = async (): Promise<GoogleFlaskToggles | undefined> => {
  try {
    const toggles = await new Promise<GoogleChromeToggles>((resolve, reject) => {
      Api.get(GOOGLE_CHROME_TOGGLES_URL, resolve, () => reject(new Error("failed to read google chrome toggles")));
    });
    return {
      allow_insecure_chrome: !!toggles.google_allow_insecure_chrome,
      classroom_extension_login: !!toggles.google_classroom_extension_login,
    };
  } catch {
    return undefined;
  }
};

const persistGoogleFlaskToggles = async (allowInsecureChrome: boolean, classroomExtensionLogin: boolean): Promise<void> => {
  const body: GoogleChromeToggles = {
    google_allow_insecure_chrome: allowInsecureChrome,
    google_classroom_extension_login: classroomExtensionLogin,
  };
  await Api.postAsync(GOOGLE_CHROME_TOGGLES_URL, body);
};

export const GoogleConfiguration: React.FC = () => {
  const [tokenExists, setTokenExists] = useState<boolean>(false);
  // False when Flask couldn't be read on load — the two Flask-only toggles are then
  // shown disabled and left untouched by the save.
  const [togglesReadable, setTogglesReadable] = useState<boolean>(true);
  const { t } = useTranslation();
  const { successToast, warningToast, errorToast } = useToast();
  const [showConfirmModal, setShowConfirmModal] = useState<boolean>(false);
  const [showPurgeModal, setShowPurgeModal] = useState<boolean>(false);
  const [isPurging, setIsPurging] = useState<boolean>(false);
  const isSupportAdmin = useSessionStore.getState().isSupportAdmin();

  const _handleLoad = useCallback(async (): Promise<GoogleConfigInputs> => {
    // SM-API: resolve the singleton GOOGLE sync node and map it into the form shape.
    // The Flask toggle read doesn't depend on the node, so run the two together.
    const [node, toggles] = await Promise.all([getSyncByType(SyncType.Google), readGoogleFlaskToggles()]);
    const base: GoogleConfigInputs = node
      ? mapNodeToGoogleInputs(node)
      : {
          enabled: false,
          sync_ous: false,
          allow_insecure_chrome: false,
          classroom_extension_login: false,
          sync_classrooms: false,
          sync_by_id: false,
          admin_username: "",
          oauth_client_id: "",
          oauth_client_secret: "",
          ignore_empty_groups: false,
          ignore_empty_classrooms: false,
          sync_id: "",
          strip_domain: false,
          strip_domain_values: undefined,
          domain_filter: false,
          domain_filter_values: undefined,
          ou_filter: false,
          ou_filter_value: undefined,
          user_merge_key: UserMergeKey.Email,
          user_merge_priority: 50,
          user_merge_expression: undefined,
          user_merge_attach_only: false,
        };
    setTokenExists(node ? isCredentialLinked(node) : false);
    // Flask unreadable: fall back to unticked for display, but record that the real
    // values are unknown so the save leaves them alone instead of writing these back.
    setTogglesReadable(toggles !== undefined);
    return { ...base, ...(toggles ?? { allow_insecure_chrome: false, classroom_extension_login: false }) };
  }, []);

  const {
    register,
    handleSubmit,
    watch,
    reset,
    setValue,
    control,
    formState: { errors, isDirty, isSubmitting, isLoading, dirtyFields },
  } = useForm<GoogleConfigInputs>({
    mode: "onBlur",
    defaultValues: _handleLoad, // we use an async func here so the form "isLoading" state is true while doing initial load
  });

  const enabled = watch("enabled");
  const domainFilter = watch("domain_filter");
  const stripDomain = watch("strip_domain");
  const ouFilter = watch("ou_filter");
  const ignoreEmptyClassrooms = watch("ignore_empty_classrooms");
  const ignoreEmptyGroups = watch("ignore_empty_groups");
  const mergeKey = watch("user_merge_key");
  const syncId = watch("sync_id");

  // eslint-disable-next-line @typescript-eslint/no-unsafe-call,@typescript-eslint/no-unsafe-member-access
  const device: string = SessionStore.getSelectedDevice() as string;

  const breadcrumbs = [
    { title: t("Configuration"), url: "/config", isActive: false },
    { title: t("Authentication"), url: "/config/device/authentication", isActive: false },
    { title: t("Google"), isActive: true },
  ];

  const handleLoad = useCallback(async (): Promise<void> => {
    reset(await _handleLoad());
  }, [reset, _handleLoad]);

  useEffect(() => {
    if (ouFilter) {
      setValue("ignore_empty_groups", true, { shouldTouch: true, shouldValidate: true, shouldDirty: true });
      setValue("ignore_empty_classrooms", true, { shouldTouch: true, shouldValidate: true, shouldDirty: true });
    }
  }, [setValue, ouFilter]);

  const _submit = useCallback(
    (input: GoogleConfigInputs) => {
      return new Promise<void>((resolve, reject) => {
        // SM-API owns the config and is the authoritative write: a failure here fails
        // the save. The two FZ-auth toggles live on in Flask until it's retired, so that
        // write is best-effort — contained below so it can never reject the save, and
        // skipped entirely unless a toggle changed (it's a whole-config read-modify-write).
        saveGoogleSync(input)
          .then(async (): Promise<boolean> => {
            const togglesDirty = !!dirtyFields.allow_insecure_chrome || !!dirtyFields.classroom_extension_login;
            if (!togglesReadable || !togglesDirty) return true;
            try {
              await persistGoogleFlaskToggles(input.allow_insecure_chrome, input.classroom_extension_login);
              return true;
            } catch {
              return false;
            }
          })
          .then(async (togglesSaved) => {
            // The sync-api write has landed either way — say so, and name the part that
            // didn't rather than reporting a save that mostly succeeded as a failure.
            if (togglesSaved) {
              successToast({ title: t("Saved Google configuration") });
            } else {
              warningToast({
                title: t("Saved Google configuration"),
                description: t(
                  "FZ Auth API and Chrome Extension Authentication could not be updated. Other changes were saved. Please try again."
                ),
              });
            }
            await handleLoad();
            resolve();
          })
          .catch((e) => {
            errorToast({
              title: t("Failed to save Google configuration"),
              description: t(extractSyncError(e, "An unknown error occurred while trying to save configuration.")),
            });
            reject("save failed");
          });
      });
    },
    [handleLoad, successToast, warningToast, errorToast, t, dirtyFields, togglesReadable]
  );

  const onSubmit: SubmitHandler<GoogleConfigInputs> = async (data) => {
    // If we are already showing the modal, submit it
    if (showConfirmModal) {
      setShowConfirmModal(false);
      return _submit(data);
    }
    // show confirmation model if an advance config was changed
    if (
      dirtyFields.strip_domain ||
      dirtyFields.strip_domain_values ||
      dirtyFields.ou_filter ||
      dirtyFields.ou_filter_value ||
      dirtyFields.ignore_empty_classrooms ||
      dirtyFields.ignore_empty_groups
    ) {
      setShowConfirmModal(true);
      return;
    }
    return _submit(data);
  };

  const handleSubmitConfirmationConfirm = () => {
    void handleSubmit(onSubmit)();
  };

  const handleRevoke = () => {
    void (async () => {
      try {
        if (syncId) await revokeCredential(syncId);
        await handleLoad();
      } catch (e) {
        errorToast({
          title: t("Failed to revoke Google credentials"),
          description: t(extractSyncError(e, "Could not revoke credentials. Please try again.")),
        });
      }
    })();
  };

  const handleLink = () => {
    // SM-API OAuth: start returns an authorization URL + single-use state to persist.
    void (async () => {
      if (!syncId) {
        errorToast({
          title: t("Save the configuration first"),
          description: t("Please save the sync configuration before linking credentials."),
        });
        return;
      }
      try {
        const redirectURI = `${window.location.origin}/config/login/admin/google/callback`;
        const auth = await startOAuth(syncId, redirectURI);
        localStorage.setItem("oauth_state_GOOGLE", auth.state);
        window.location.href = auth.authorizationURL;
      } catch (e) {
        errorToast({
          title: t("Failed to link Google credentials"),
          description: t(extractSyncError(e, "Could not start credential linking. Please try again.")),
        });
      }
    })();
  };

  const handleSync = useCallback(() => {
    void (async () => {
      if (syncId) {
        await executeSync(syncId).catch(() =>
          errorToast({ title: t("Failed to start sync"), description: t("The sync could not be started. Please try again.") })
        );
      }
      await handleLoad();
    })();
    const manuallySetRunningSyncs = JSON.parse(localStorage.getItem("manuallySetRunningSyncs") || "{}");
    const now = new Date();
    // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
    manuallySetRunningSyncs[device.concat("_GoogleLockout")] = now.getTime() + 60000; // 60 second 'ttl' to lock out this sync in this browser
    localStorage.setItem("manuallySetRunningSyncs", JSON.stringify(manuallySetRunningSyncs));
  }, [device, handleLoad, syncId, errorToast, t]);

  const handlePurge = useCallback(async () => {
    setIsPurging(true);
    try {
      await purgeSyncData(syncId, device);
      successToast({
        title: t("Purge triggered successfully"),
      });
      setShowPurgeModal(false);
      await handleLoad();
    } catch (error) {
      errorToast({
        title: t("Failed to purge data"),
        description: t(error instanceof Error ? error.message : "An unknown error occurred while trying to purge data."),
      });
    } finally {
      setIsPurging(false);
    }
  }, [syncId, device, successToast, errorToast, t, handleLoad]);

  return (
    <PageWithHeader title={"Google"} breadcrumbs={breadcrumbs}>
      <Flex mr="sp24" justifyContent="end">
        {isSupportAdmin && syncId && (
          <Button variant="danger" onClick={() => setShowPurgeModal(true)} data-testid="purge-btn">
            Purge Data
          </Button>
        )}
      </Flex>
      <Box display={"flex"} flex={1} padding={"24px"} gap={"16px"}>
        <Box flex={1} minWidth={"500px"}>
          <Box background={"white"} display={"flex"} flexDir={"column"} gap={"1rem"} padding={"1rem"} borderRadius={"6px"}>
            <Box padding={"20px 20px 0 20px"} display={"flex"} flexDir={"row"} gap={"4px"}>
              <Text fontSize={14}>
                {"Find out how to"}{" "}
                <Link isExternal href={"https://help.linewize.com/hc/en-gb/articles/5732845239580"}>
                  sync Google with Linewize Filter
                </Link>
              </Text>
            </Box>
            {isLoading && (
              <Box display={"flex"} justifyContent={"center"} flex={1}>
                <DumbBusyIndicator loaded={!isLoading} />
              </Box>
            )}
            {!isLoading && (
              // eslint-disable-next-line @typescript-eslint/no-misused-promises
              <form onSubmit={handleSubmit(onSubmit)} data-testid={"google-config-form"}>
                <Box sx={{ display: "flex", flexDirection: "column", padding: "24px", gap: "16px", fontSize: "14px" }}>
                  <CheckBoxControl
                    fieldProps={{
                      name: "enabled",
                      label: "Enabled",
                      tooltip: "When ticked, this Google sync will run.",
                      control: control,
                      errors: errors,
                    }}
                  />

                  <Divider borderColor="neutrals.40" />
                  {enabled && (
                    <>
                      {!togglesReadable && (
                        <InlineNotification
                          status="warning"
                          notificationTitle={"Some settings could not be loaded."}
                          notificationDescription={
                            "FZ Auth API and Chrome Extension Authentication are unavailable right now, so they can't be changed. Their current values are unaffected by saving. Reload the page to try again."
                          }
                        />
                      )}
                      <CheckBoxControl
                        fieldProps={{
                          name: "allow_insecure_chrome",
                          label: "Use FZ Auth API",
                          control: control,
                          errors: errors,
                          disabled: !togglesReadable,
                          tooltip: "Turn on to use Linewize Cloud Authentication instead of Google OAuth.",
                        }}
                      />

                      <CheckBoxControl
                        fieldProps={{
                          name: "classroom_extension_login",
                          label: "Allow Chrome Extension Authentication",
                          control: control,
                          errors: errors,
                          disabled: !togglesReadable,
                          tooltip: "Turn on to use Linewize Connect for Chrome to authenticate a user's device.",
                        }}
                      />

                      <Divider borderColor="neutrals.40" />
                      <CheckBoxControl
                        fieldProps={{
                          name: "sync_ous",
                          label: "Sync Organization Units",
                          control: control,
                          errors: errors,
                          tooltip: "Turn on to sync any existing organizational units in your Google Workspace.",
                        }}
                      />
                      <CheckBoxControl
                        fieldProps={{
                          name: "sync_classrooms",
                          label: "Sync Classrooms",
                          control: control,
                          errors: errors,
                          tooltip:
                            "Turn on to let Google Classrooms, including assigned students and teachers, sync with Classroom Manager.",
                        }}
                      />
                      <CheckBoxControl
                        fieldProps={{
                          name: "sync_by_id",
                          label: "Sync Groups By ID",
                          control: control,
                          errors: errors,
                          tooltip:
                            "Syncing by ID allows you to sync multiple groups with the same name, and strip domain doesn't apply on groups anymore. Changing this can invalidate existing filtering rules.",
                        }}
                      />

                      <CheckboxTextComboControl
                        label="Domain Filter"
                        tooltip="Accepts CSV list of domains. Only users and groups within these domains will be synced."
                        control={control}
                        errors={errors}
                        checkboxFieldProps={{
                          name: "domain_filter",
                        }}
                        inputFieldProps={{
                          name: "domain_filter_values",
                          disabled: !domainFilter,
                          rules: {
                            required: domainFilter ? "Domains required if Domain Filter is enabled." : false,
                            minLength: { value: 3, message: "Minimum length should be 3" },
                            pattern: {
                              value: /^(?!.*:\/\/)(([^,\s]+\s*,\s*)*[^,\s]+)$/,
                              message: "Please enter domains without protocols, separated by commas.",
                            },
                          },
                        }}
                      />

                      <TextFieldControl
                        fieldProps={{
                          name: "admin_username",
                          label: "Administrator",
                          control: control,
                          errors: errors,
                          tooltip: "Enter the email address of your Google Workspace Super Administrator account.",
                          rules: {
                            required: "A administrator account address is required.",
                          },
                        }}
                      />
                      <Accordion
                        allowToggle
                        defaultIndex={stripDomain || ouFilter || ignoreEmptyClassrooms || ignoreEmptyGroups ? 0 : undefined}
                        sx={{
                          border: `1px solid ${colors.neutrals["20"]}`,
                          borderRadius: "6px",
                        }}
                      >
                        <AccordionItem>
                          <AccordionButton data-testid={"accordionBtn"}>
                            <Box
                              sx={{
                                display: "flex",
                                width: "100%",
                                minHeight: "40px",
                                backgroundColor: colors.neutrals["20"],
                                alignContent: "center",
                                alignItems: "center",
                                padding: "12px",
                                gap: "8px",
                              }}
                            >
                              <AccordionIcon />

                              <Text fontWeight={500}>Advanced Configuration</Text>
                            </Box>
                          </AccordionButton>
                          <AccordionPanel
                            sx={{
                              display: "flex",
                              flexDirection: "column",
                              padding: "16px",
                              fontSize: "14px",
                              gap: "16px",
                            }}
                          >
                            <Text>
                              Advanced sync settings are for complex deployments only. Consult with the Linewize support team before making
                              any changes.
                            </Text>
                            <Divider borderColor="neutrals.40" />
                            <MergeOptions errors={errors} register={register} control={control} mergeKey={mergeKey} />
                            <Divider borderColor="neutrals.40" />

                            {stripDomain && (
                              <InlineNotification
                                status="warning"
                                notificationTitle={"User names need to be unique when using strip domains. "}
                                notificationDescription={
                                  "Strip Domain is used to modify usernames from the directory. Before enabling this feature ensure usernames without domains are unique. If usernames are not unique this will result in errors."
                                }
                              />
                            )}

                            <CheckboxTextComboControl
                              label="Strip Domain"
                              tooltip="Accepts CSV list of domains. Users within these domains will be imported as `username` instead of `username@domain.com`."
                              control={control}
                              errors={errors}
                              checkboxFieldProps={{
                                name: "strip_domain",
                              }}
                              inputFieldProps={{
                                name: "strip_domain_values",
                                disabled: !stripDomain,
                                rules: {
                                  required: stripDomain ? "Domains are required if Strip Domain is enabled." : false,
                                  minLength: { value: 3, message: "Minimum length should be 3" },
                                  pattern: {
                                    value: /^(?!.*:\/\/)(([^,\s]+\s*,\s*)*[^,\s]+)$/,
                                    message: "Please enter domains without a protocol.",
                                  },
                                },
                              }}
                            />

                            {ouFilter && (
                              <InlineNotification
                                status="warning"
                                notificationTitle={"Enabling Organization Unit filter forces ignore empty groups and classrooms to be on."}
                                notificationDescription={
                                  "While Organization Unit Filtering is enabled, both ignore empty classrooms and ignore empty groups will be enabled by default."
                                }
                              />
                            )}

                            <CheckboxTextComboControl
                              label="Organization Unit Filter"
                              tooltip="Only import users from specific Organizational Units."
                              control={control}
                              errors={errors}
                              checkboxFieldProps={{
                                name: "ou_filter",
                              }}
                              inputFieldProps={{
                                name: "ou_filter_value",
                                disabled: !ouFilter,
                                rules: {
                                  required: ouFilter ? "OU required if Organization Unit Filter is enabled." : false,
                                  minLength: { value: 3, message: "Minimum length should be 3" },
                                },
                              }}
                            />
                            <Divider borderColor="neutrals.40" />

                            <CheckBoxControl
                              fieldProps={{
                                name: "ignore_empty_groups",
                                label: "Ignore Empty Groups",
                                tooltip: "When ticked, empty groups won't be synced.",
                                control: control,
                                errors: errors,
                                disabled: ouFilter,
                              }}
                            />
                            <CheckBoxControl
                              fieldProps={{
                                name: "ignore_empty_classrooms",
                                label: "Ignore Empty Classrooms",
                                tooltip: "When ticked, empty classrooms won't be synced.",
                                control: control,
                                errors: errors,
                                disabled: ouFilter,
                              }}
                            />
                          </AccordionPanel>
                        </AccordionItem>
                      </Accordion>
                    </>
                  )}
                </Box>

                <Box
                  sx={{
                    padding: "24px",
                    background: "#F5FCFF",
                    display: "flex",
                    gap: "24px",
                    alignItems: "center",
                  }}
                >
                  <Button
                    data-testid={"submitBtn"}
                    mt={4}
                    disabled={!isDirty || isSubmitting}
                    colorScheme="teal"
                    type="submit"
                    variant={"primary"}
                  >
                    Submit
                  </Button>
                  <DumbBusyIndicator loaded={!isSubmitting} />
                </Box>
                <AdvanceConfigurationConfirmModal
                  isOpen={showConfirmModal}
                  onClose={() => setShowConfirmModal(false)}
                  onSubmit={handleSubmitConfirmationConfirm}
                />
              </form>
            )}
          </Box>
        </Box>
        {enabled && (
          <Box width={"400px"} gap={"16px"} display={"flex"} flexDir={"column"}>
            <SyncStatus
              syncType={"GOOGLE"}
              syncId={syncId || undefined}
              handleRunSync={tokenExists && !isDirty ? handleSync : undefined}
              syncLockoutTime={
                device
                  ? // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
                    JSON.parse(localStorage.getItem("manuallySetRunningSyncs") || "{}")[device.concat("_GoogleLockout")]
                    ? // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
                      JSON.parse(localStorage.getItem("manuallySetRunningSyncs") || "{}")[device.concat("_GoogleLockout")]
                    : undefined
                  : undefined
              }
            />
            <SyncCredentialStatus
              isLinked={tokenExists}
              handleLink={handleLink}
              handleRevoke={handleRevoke}
              syncType={"GOOGLE"}
              linkDisabled={!syncId || isDirty}
              linkDisabledReason={
                !syncId ? "Save the sync configuration before linking credentials." : "Save your changes before linking credentials."
              }
            />
          </Box>
        )}
      </Box>
      <PurgeConfirmModal
        isOpen={showPurgeModal}
        onClose={() => setShowPurgeModal(false)}
        onConfirm={() => void handlePurge()}
        isLoading={isPurging}
      />
    </PageWithHeader>
  );
};
